import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiError, api, tokens } from '@shared/api';
import { AuroraBackdrop, BrandMark, ModelLogo } from '@shared/brand';
import { Alert, Icon, Segmented, ThemeToggle } from '../components/ui';
import '../styles/login.css';

interface AuthResponse {
  access_token: string;
  refresh_token: string;
}

type Mode = 'login' | 'register';

const LOGOS = [
  { model: 'claude', label: 'Claude' },
  { model: 'gpt', label: 'GPT' },
  { model: 'gemini', label: 'Gemini' },
  { model: 'grok', label: 'Grok' },
  { model: 'deepseek', label: 'DeepSeek' },
  { model: 'kimi', label: 'Kimi' },
  { model: 'glm', label: 'GLM' },
  { model: 'seedance', label: 'Seedance' },
];

const FACTS = [
  { title: 'Pakai kode yang sudah ada', text: 'Format API mengikuti OpenAI. Cukup ganti alamat dan key.' },
  { title: 'Biaya terkendali', text: 'Batas anggaran per proyek dan per key. Setiap request tercatat.' },
  { title: 'Bayar dalam Rupiah', text: 'Isi saldo lewat QRIS, virtual account, atau e‑wallet.' },
];

export function Login({ onAuthenticated }: { onAuthenticated: () => void }) {
  const [params] = useSearchParams();
  const [mode, setMode] = useState<Mode>(params.get('daftar') ? 'register' : 'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [name, setName] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const switchMode = (next: Mode) => {
    setMode(next);
    setError(null);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const body =
        mode === 'login'
          ? { email, password }
          : { email, password, name, organization_name: organizationName || undefined };
      const result = await api<AuthResponse>(`/v1/auth/${mode}`, { method: 'POST', body });
      tokens.set(result.access_token, result.refresh_token);
      onAuthenticated();
    } catch (err) {
      setError(err instanceof ApiError ? friendly(err, mode) : 'Server tidak bisa dihubungi. Periksa koneksi Anda lalu coba lagi.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-split">
      <section className="auth-art">
        <AuroraBackdrop />
        <a href="/" className="brand login-art-brand" style={{ padding: 0 }}>
          <BrandMark sub="AI gateway" />
        </a>

        <div className="login-hero">
          <h1 className="login-headline">Satu API untuk Claude, GPT, Gemini, DeepSeek, dan model lainnya.</h1>
          <p className="login-lede">Satu API key dan satu saldo untuk semua model, dengan harga dan tagihan dalam Rupiah.</p>

          <ul className="login-logos" aria-label="Model yang tersedia">
            {LOGOS.map((logo) => (
              <li key={logo.model} title={logo.label}>
                <ModelLogo model={logo.model} size={40} />
                <span>{logo.label}</span>
              </li>
            ))}
          </ul>

          <dl className="login-facts">
            {FACTS.map((fact) => (
              <div key={fact.title}>
                <dt>{fact.title}</dt>
                <dd>{fact.text}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section className="auth-form">
        <div className="login-top">
          {/* phones only */}
          <a href="/" className="brand login-brand">
            <BrandMark />
          </a>
          <ThemeToggle />
        </div>
        <div className="auth-card">
          <h1 className="login-title">{mode === 'login' ? 'Masuk ke portal' : 'Buat akun'}</h1>
          <p className="subtitle login-sub">
            {mode === 'login'
              ? 'Kelola API key, saldo, dan pemakaian tim Anda.'
              : 'Gratis. Anda hanya membayar pemakaian dari saldo.'}
          </p>

          <div className="login-seg">
            <Segmented
              label="Masuk atau daftar"
              options={[
                { id: 'login', label: 'Masuk' },
                { id: 'register', label: 'Daftar' },
              ]}
              value={mode}
              onChange={switchMode}
            />
          </div>

          {error && <Alert kind="error">{error}</Alert>}

          <form onSubmit={submit}>
            {mode === 'register' && (
              <>
                <div className="field">
                  <label htmlFor="name">Nama</label>
                  <input id="name" value={name} onChange={(e) => setName(e.target.value)} required autoComplete="name" placeholder="Nama lengkap" />
                </div>
                <div className="field">
                  <label htmlFor="org">
                    Perusahaan <span className="login-optional">opsional</span>
                  </label>
                  <input
                    id="org"
                    value={organizationName}
                    onChange={(e) => setOrganizationName(e.target.value)}
                    placeholder="Nama perusahaan atau tim"
                    autoComplete="organization"
                  />
                </div>
              </>
            )}

            <div className="field">
              <label htmlFor="email">Email kerja</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                placeholder="nama@perusahaan.co.id"
              />
            </div>

            <div className="field">
              <div className="row between">
                <label htmlFor="password">Kata sandi</label>
                <button type="button" className="login-peek" onClick={() => setShowPassword((v) => !v)}>
                  {showPassword ? 'Sembunyikan' : 'Tampilkan'}
                </button>
              </div>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={mode === 'register' ? 10 : undefined}
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              />
              {mode === 'register' && (
                <span className="field-hint">
                  Minimal 10 karakter{password.length > 0 && password.length < 10 ? ` (kurang ${10 - password.length})` : ''}.
                </span>
              )}
            </div>

            <button className="btn btn-primary btn-lg btn-block" disabled={busy}>
              {busy ? <span className="spinner" /> : mode === 'login' ? 'Masuk' : 'Buat akun'}
            </button>
          </form>

          <Link to="/docs" className="login-docs">
            <Icon name="book" size={16} />
            <span className="grow">Baca dokumentasi API tanpa login</span>
            <Icon name="arrow" size={15} />
          </Link>
        </div>
      </section>
    </div>
  );
}

function friendly(error: ApiError, mode: Mode): string {
  if (error.status === 401) return 'Email atau kata sandi salah.';
  if (error.status === 409) return 'Email ini sudah terdaftar. Pilih Masuk untuk login.';
  if (error.status === 400) {
    return mode === 'register' ? 'Periksa isian Anda. Kata sandi minimal 10 karakter.' : 'Periksa kembali email dan kata sandi.';
  }
  if (error.status === 429) return 'Terlalu banyak percobaan. Tunggu satu menit lalu coba lagi.';
  if (error.status >= 500) return 'Server sedang bermasalah. Coba lagi sebentar lagi.';
  return error.message;
}
